import React from 'react';
import classNames from 'classnames';

import { action, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { Link, Route, Switch } from 'react-router-dom';

import {
  Appearance as StripeAppearance,
  StripeElementsOptions,
  StripeElementLocale,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';

import {
  Elements as StripeElementsProvider,
  PaymentElement as StripePaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { FormSubmitEvent } from 'ui/base/types';
import { BusyStore } from 'ui/base/button';
import { BooleanStore, ChoiceStore, StringStore } from 'ui/base/input';
import { StandardPage } from 'ui/base/page/standard_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { RadioInput } from 'ui/standard/input/radio_input';

import { StripePaymentElementProgress } from 'ui/vendor/stripe/stripe';

import { ActiveButton, DefaultButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { http } from 'services/base/http';
import { sleep } from 'services/base/time';
import { Optional } from 'services/base/types';
import { PLATFORM } from 'services/base/config';
import { generateUUID } from 'services/base/uuid';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { useStripePromise } from 'services/base/stripe';
import { pushPaymentRedirect } from 'services/base/redirect';
import { PaymentRedirectType } from 'services/base/redirect_types';

import { Payment, PlanFirewallBilling, PlanFirewallPackage, PlanType } from 'services/accounts/types';
import { PaymentsService } from 'services/accounts/payments_service';
import { PlanFirewallService } from 'services/accounts/plans_service';

import {
  getPaymentMethodInfo,
  getPaymentMethodName,
  getPaymentMethodPlatform,
} from 'services/accounts/logic/payments/payments';

import {
  getPlanFirewallPackageTier,
  getPlanFirewallSubscriptionActive,
} from 'services/accounts/logic/plans/plan_firewall';

import { Currency } from 'assets/enums/currencies/currencies';

import { PlanItemFirewallBaseOptionalModalProps } from '../plan_item_firewall';

import ManagePlansMessages from '../../../../manage_plans_messages';
import PlanFirewallMessages from '../plan_item_firewall_messages';
import Messages from './plan_firewall_payment_modal_messages';

import 'flag-icons/css/flag-icons.min.css';

declare global {
  interface Window {
    paymentRedirectCallback: Optional<() => void>;
  }
}

const themedStripeStyles: ThemedStyles<StripeAppearance> = {
  [Theme.DAYLIGHT]: {
    theme: 'stripe',
    variables: {
      borderRadius: '10px',
      colorBackground: '#fcefe6',
      colorDanger: '#ffc107',
      colorIconCardCvc: '#000000',
      colorText: '#000000',
      colorPrimary: '#000000',
      colorTextSecondary: '#656580',
      colorTextPlaceholder: '#656580',
      fontSizeSm: '12px',
    },
    rules: {
      '.AccordionItem': {
        border: '1px solid #000000',
        backgroundColor: 'transparent',
      },
      '.AccordionItem--selected': {
        border: '1px solid #e97937',
        backgroundColor: '#fcefe6',
      },
      '.Label': {
        color: 'var(--colorTextPlaceholder)',
        textTransform: 'uppercase',
      },
      '.Block': {
        border: '1px solid #e97937',
      },
      '.Input': {
        border: 'none',
        borderRadius: '0',
        borderBottom: '1px solid var(--colorTextSecondary)',
        backgroundColor: '#fcefe6',
        padding: '10px 0px 10px 0px',
      },
      '.Input:focus': {
        outline: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid #e97937',
      },
      '.Input--invalid': {
        boxShadow: 'none',
        borderBottom: '1px solid var(--colorDanger)',
      },
      '.Input::placeholder': {
        color: 'var(--colorTextSecondary)',
      },
      '.CheckboxInput': {
        border: '1px solid #e97937',
      },
      '.CheckboxInput--checked': {
        border: '1px solid #e97937',
        backgroundColor: '#e97937',
      },
    },
  },
  [Theme.MIDNIGHT]: {
    theme: 'stripe',
    variables: {
      borderRadius: '10px',
      colorBackground: '#385ba4',
      colorDanger: '#ffc107',
      colorIconCardCvc: '#ffffff',
      colorText: '#ffffff',
      colorPrimary: '#ffffff',
      colorTextSecondary: '#d3d3d3',
      colorTextPlaceholder: '#d3d3d3',
      fontSizeSm: '12px',
    },
    rules: {
      '.AccordionItem': {
        border: '1px solid #ffffff',
        backgroundColor: 'transparent',
      },
      '.AccordionItem--selected': {
        border: '1px solid #00cbfd',
        backgroundColor: '#385ba4',
      },
      '.Label': {
        color: 'var(--colorTextPlaceholder)',
        textTransform: 'uppercase',
      },
      '.Block': {
        border: '1px solid #00cbfd',
      },
      '.Input': {
        border: 'none',
        borderRadius: '0',
        borderBottom: '1px solid var(--colorTextSecondary)',
        backgroundColor: '#385ba4',
        padding: '10px 0px 10px 0px',
      },
      '.Input:focus': {
        outline: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid #00cbfd',
      },
      '.Input--invalid': {
        boxShadow: 'none',
        borderBottom: '1px solid var(--colorDanger)',
      },
      '.Input::placeholder': {
        color: 'var(--colorTextSecondary)',
      },
      '.CheckboxInput': {
        border: '1px solid #00cbfd',
      },
      '.CheckboxInput--checked': {
        border: '1px solid #00cbfd',
        backgroundColor: '#00cbfd',
      },
    },
  },
};

type PlanItemFirewallPaymentInputRowProps = PlanItemFirewallBaseOptionalModalProps & {
  choices: ChoiceStore<Payment>;
  choice: string;
  payment_name: string;
  payment_platform: string;
  payment_icon: string;
  payment_info: string;
};
const PlanItemFirewallPaymentInputRow = observer(({ styles, choices, choice, payment_name, payment_platform, payment_icon, payment_info } : PlanItemFirewallPaymentInputRowProps) => {
  const selected = choice === choices.selected;

  const choice_store = React.useCallback(action(() => {
    choices.setSelected(choice);
  }), [choices]);

  return (
    <div
      className={classNames(styles.field, styles.clickable,
                            selected ? styles.cardSelected :
                            styles.cardDefault)}
      onClick={choice_store}>
      <img className={styles.cardIcon} src={payment_icon} alt='Payment Icon' />
      <div className={styles.cardText}>
        <div className={styles.cardName}>
          {payment_name}
          <span className={styles.cardInfo}>
            {ManagePlansMessages.labelPayment(payment_platform)}
          </span>
        </div>
        <div className={styles.cardInfo}>
          {payment_info}
        </div>
      </div>
      <RadioInput
        store={choices}
        choice={choice}
      />
    </div>
  );
});

type PlanItemFirewallPaymentStripeFormProps = PlanItemFirewallBaseOptionalModalProps & {
  busy: BusyStore;
  error: StringStore;
  className?: string;
  children?: React.ReactNode;
};
const PlanItemFirewallPaymentStripeForm = observer((props: PlanItemFirewallPaymentStripeFormProps) => {
  const { accounts, pangolin, store, modal, root, busy, error, className, children } = props;

  const stripe = useStripe();
  const elements = useElements();

  // Handle payment redirect (platform: mobile)

  const [redirectPackage, setRedirectPackage] = React.useState<Optional<PlanFirewallPackage>>(undefined);
  const [redirectBilling, setRedirectBilling] = React.useState<Optional<PlanFirewallBilling>>(undefined);
  const [redirecting, setRedirecting] = React.useState(false);
  const [redirected, setRedirected] = React.useState(false);

  React.useEffect(() => reaction(
    () => [redirecting, redirected],
    ([redirecting, redirected]) => {
      if (!redirecting) return;
      if (redirected) return;
      store.package_store.setSelected(redirectPackage);
      store.billing_store.setSelected(redirectBilling);
      setRedirectPackage(undefined);
      setRedirectBilling(undefined);
      setRedirected(true);
    }, { fireImmediately: false }
  ));

  // Handle payment method form submission

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    error.setValue('');
    if (stripe && elements) {
      const result = await busy.perform(async() => {
        try {
          modal.freezeModal();

          // Trigger form validation and wallet collection
          const submitResponse = await elements.submit();

          // Return any errors encountered during form validation
          if (submitResponse.error) {
            return submitResponse.error;
          }

          // Create the SetupIntent and obtain clientSecret
          const clientSecret = (
            await http.post('/billing/backend/stripe/setup')
                            .then((response) => response.data)
                            .catch(() => null)
          );

          // Determine the history stack to restore on return
          const historyStack = modal.history.stack.map(l => l.pathname);
          const historyStackSlice = historyStack.slice(0, historyStack.indexOf(`${root}/payment`) + 1);

          if (PLATFORM === 'web') {
            // Generate a redirect ID to identify the saved context
            const redirectID = generateUUID();

            // Construct the return URL
            const returnUrl = `${window.location.origin}?payment-redirect=${redirectID}`;

            // Save the context to restore on return
            pushPaymentRedirect({
              id: redirectID,
              type: PaymentRedirectType.PLAN,
              pangolin: pangolin.id,
              plan: PlanType.FIREWALL,
              package: store.package_store.value,
              billing: store.billing_store.value,
              history: historyStackSlice,
              lastPaymentsCount: accounts.payments.size,
            });

            // Confirm the SetupIntent using the details collected by the Payment Element
            const confirmResponse = await stripe.confirmSetup({
              elements, clientSecret, confirmParams: {
                return_url: returnUrl,
              },
            });

            // Return any errors encountered during SetupIntent confirmation
            if (confirmResponse.error) {
              return confirmResponse.error;
            }
          } else {
            // Construct the return URL
            const returnUrl = 'payment://redirect/';

            // Register callback handling return to payment selection
            window.paymentRedirectCallback = () => {
              (async() => {
                let first = true;
                const end = Date.now() + 5000;
                const lastPaymentsCount = accounts.payments.size;
                while (Date.now() <= end && accounts.payments.size === lastPaymentsCount) {
                  // eslint-disable-next-line no-await-in-loop
                  await PaymentsService.fetchAll(accounts);
                  // eslint-disable-next-line no-await-in-loop
                  if (!first) await sleep(1000);
                  first = false;
                }
                setRedirectPackage(store.package_store.value);
                setRedirectBilling(store.billing_store.value);
                setRedirecting(true);
                const offset = historyStack.length - historyStackSlice.length;
                for (let i = 0; i < offset; i++) modal.history.goBack();
                window.paymentRedirectCallback = undefined;
              })();
            };

            // Confirm the SetupIntent using the details collected by the Payment Element
            const confirmResponse = await stripe.confirmSetup({
              elements, clientSecret, confirmParams: {
                return_url: returnUrl,
              },
            });

            // Return any errors encountered during SetupIntent confirmation
            if (confirmResponse.error) {
              return confirmResponse.error;
            }
          }
        } finally {
          modal.unfreezeModal();
        }
      });

      error.setValue(result?.message ? result.message :
                     PlanFirewallMessages.errorGeneric());
    }
  }, [modal, accounts, pangolin, root, busy, store, error, stripe, elements]);

  return (
    <form className={className} onSubmit={onSubmit}>
      {children}
    </form>
  );
});

type PlanItemFirewallPaymentStripeModalProps = PlanItemFirewallBaseOptionalModalProps;
const PlanItemFirewallPaymentStripeModal = observer((props: PlanItemFirewallPaymentStripeModalProps) => {
  const { styles, modal } = props;

  const appearance = useStyles(themedStripeStyles);

  const stripePromise = useStripePromise();

  const globalLocale = GlobalLocaleStore.get();

  const [ready] = React.useState(new BooleanStore(false));
  const [busy] = React.useState(new BusyStore());
  const [error] = React.useState(new StringStore(''));

  const currency_default = Currency.USD;
  const currency_choices: Record<Currency, Currency> = {
    [Currency.USD]: Currency.USD,
    [Currency.EUR]: Currency.EUR,
    [Currency.JPY]: Currency.JPY,
    [Currency.TWD]: Currency.TWD,
  };
  const [currency_store] = React.useState(
    new ChoiceStore<Currency>(currency_choices, currency_default)
  );

  const newLocale = React.useCallback(action((): StripeElementLocale => {
    switch (globalLocale) {
      case 'zh_TW': return 'zh-TW';
      case 'ja_JP': return 'ja';
      default: return 'en';
    }
  }), [globalLocale]);

  const [locale, setLocale] = React.useState<StripeElementLocale>(newLocale);
  React.useEffect(() => { setLocale(newLocale); }, [newLocale]);

  const newProviderOptions = React.useCallback(action((): StripeElementsOptions => {
    return {
      mode: 'setup', loader: 'never', appearance, locale,
      currency: currency_store.selected!.toLowerCase(),
      paymentMethodTypes: ['card'],
    };
  }), [appearance, locale, currency_store.selected]);

  const [providerOptions, setProviderOptions] = React.useState<StripeElementsOptions>(newProviderOptions);
  React.useEffect(() => { setProviderOptions(newProviderOptions); }, [newProviderOptions]);

  const newPaymentOptions = React.useCallback(action((): StripePaymentElementOptions => {
    return {
      layout: {
        type: 'accordion', radios: false,
        spacedAccordionItems: false,
        defaultCollapsed: false,
      },
    };
  }), []);

  const [paymentOptions, setPaymentOptions] = React.useState<StripePaymentElementOptions>(newPaymentOptions);
  React.useEffect(() => { setPaymentOptions(newPaymentOptions); }, [newPaymentOptions]);

  return (
    <StripeElementsProvider stripe={stripePromise} options={providerOptions}>
      <div className={classNames(styles.modalContainerLargest, styles.modalContainerPopOut)}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.stripeName()}
            <button
              className={styles.modalCloseIcon}
              onClick={modal.dismissModal}
              disabled={busy.value}
              type='button'
            />
          </div>
          <PlanItemFirewallPaymentStripeForm
            className={styles.modalContentRow}
            busy={busy} error={error} {...props}>
            <div className={styles.section}>
              {/* TODO: implement in the future */}
              {/* <div className={styles.currencies}>
                <CurrencyInput choice={Currency.USD} store={currency_store} />
                <CurrencyInput choice={Currency.EUR} store={currency_store} />
                <CurrencyInput choice={Currency.JPY} store={currency_store} />
                <CurrencyInput choice={Currency.TWD} store={currency_store} />
              </div> */}
              {!ready.value && <StripePaymentElementProgress/>}
              <StripePaymentElement options={paymentOptions} onReady={() => ready.setValue(true)}/>
              {error.value && (
                <div className={styles.notes}>
                  <ErrorMessage message={error.value} />
                </div>
              )}
            </div>
            <ActiveButton busy={busy} disabled={!ready.value}>
              {ManagePlansMessages.actionNext()}
            </ActiveButton>
            <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
              {ManagePlansMessages.actionBack()}
            </DefaultButton>
          </PlanItemFirewallPaymentStripeForm>
        </StandardPage>
      </div>
    </StripeElementsProvider>
  );
});

type PlanItemFirewallPaymentCreateModalProps = PlanItemFirewallBaseOptionalModalProps;
const PlanItemFirewallPaymentCreateModal = observer(({ styles, modal, root }: PlanItemFirewallPaymentCreateModalProps) => {
  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.addName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <Link className={classNames(styles.field, styles.item, styles.clickable)}
                  to={`${root}/payment/create/stripe`}>
              <div className={styles.fieldTextLeft} >
                {Messages.stripeName()}
                <div className={styles.fieldInfo}>
                  {Messages.stripeInfo()}
                </div>
              </div>
              <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
              <div className={styles.open}/>
            </Link>
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallPaymentHomeModalProps = PlanItemFirewallBaseOptionalModalProps;
const PlanItemFirewallPaymentHomeModal = observer((props: PlanItemFirewallPaymentHomeModalProps) => {
  const { styles, accounts, pangolin, plan, store, modal, root } = props;

  const disabled = store.payment_store.value === undefined;

  const licensePackage = plan?.license?.package;
  const subscriptionPackage = plan?.subscription?.package;
  const targetPackage = store.package_store.value;

  const payments = Object.entries(store.payment_store.choices);

  const [busy] = React.useState(new BusyStore());
  const [error, setError] = React.useState<Optional<string>>(undefined);
  const [warning, setWarning] = React.useState<Optional<string>>(undefined);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  // Handle warning
  React.useEffect(() => {
    if (plan && getPlanFirewallSubscriptionActive(plan)) {
      const targetPackageTier = getPlanFirewallPackageTier(targetPackage);
      const subscriptionPackageTier = getPlanFirewallPackageTier(subscriptionPackage);
      if (targetPackageTier <= subscriptionPackageTier) {
        // All scenarios where the current subscription is retained are
        // conditional on whether the user is purchasing a package tier
        // lower than the current subscription package tier.
        //
        // You may refer to the scenarios outlined in the previewPlanChange
        // method in: services/accounts/logic/plans/plan_firewall.ts
        if (plan.subscription) {
          setWarning(PlanFirewallMessages.warningEffectivity(
            plan.subscription.valid_until.toLocaleString(locale, format)
          ));
        }
      } else {
        setWarning(undefined);
      }
    }
  }, [plan, licensePackage, subscriptionPackage, targetPackage, locale, format]);

  // Handle navigation
  const onNext = React.useCallback(async() => {
    // Clear error string
    setError(undefined);

    // Clear preview data
    store.preview_is_trialing_store.setValue(false);
    store.preview_trial_until_store.setValue(undefined);
    store.preview_charges_store.setValue(undefined);
    store.preview_refunds_store.setValue(undefined);
    store.preview_notes_store.setValue(undefined);

    // Retrieve invoice preview
    const preview = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await PlanFirewallService.preview(
          accounts, pangolin,
          store.payment_store.value!,
          store.package_store.value!,
          store.billing_store.value!,
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (preview.success) {
      // Go to confirm modal on success
      store.preview_is_trialing_store.setValue(preview.is_trialing);
      store.preview_trial_until_store.setValue(preview.trial_until);
      store.preview_charges_store.setValue(preview.charges);
      store.preview_refunds_store.setValue(preview.refunds);
      store.preview_notes_store.setValue(preview.notes);
      modal.history.push(`${root}/confirm`);
    } else {
      // Set error string on failure
      setError(PlanFirewallMessages.errorInvoice());
    }
  }, [modal, accounts, pangolin, root, busy, store]);

  return (
    <div className={classNames(styles.modalContainerLarge, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.name()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            {
              payments.map(([payment_id, payment]) => (
                <PlanItemFirewallPaymentInputRow
                  key={payment_id}
                  payment_name={getPaymentMethodName(payment)}
                  payment_platform={getPaymentMethodPlatform(payment)}
                  payment_icon={styles.fieldIconImagePayment}
                  payment_info={getPaymentMethodInfo(payment)}
                  choices={store.payment_store}
                  choice={payment_id}
                  {...props}
                />
              ))
            }
          </div>
          <div className={styles.section}>
            <Link className={styles.link} to={`${root}/payment/create`}>
              <div className={styles.add}/>
              {Messages.addName()}
            </Link>
            {warning && (
              <div className={styles.notes}>
                <ErrorMessage message={warning} />
              </div>
            )}
            {error && (
              <div className={styles.notes}>
                <ErrorMessage message={error} />
              </div>
            )}
          </div>
          <ActiveButton busy={busy} onClick={onNext} disabled={disabled}>
            {ManagePlansMessages.actionNext()}
          </ActiveButton>
          <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallPaymentModalProps = PlanItemFirewallBaseOptionalModalProps;
export const PlanItemFirewallPaymentModal = observer((props: PlanItemFirewallPaymentModalProps) => {
  const { root } = props;
  return (
    <Switch>
      <Route exact path={`${root}/payment`}>
        <PlanItemFirewallPaymentHomeModal {...props} />
      </Route>
      <Route exact path={`${root}/payment/create`}>
        <PlanItemFirewallPaymentCreateModal {...props} />
      </Route>
      <Route exact path={`${root}/payment/create/stripe`}>
        <PlanItemFirewallPaymentStripeModal {...props} />
      </Route>
    </Switch>
  );
});
